import { useState, useEffect, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { tKeys } from '@/localization';
import { useLoginWithTokenMutation } from '@/services/auth.service';
import { buildRedirectLink } from '@/utils';

import { useUserContext } from '@/context/user.context';

import { RoutesEnum } from '@/enum/routes.enum';
import { SearchParamsEnum } from '@/enum/searchParams.enum';
import { StorageKeysEnum } from '@/enum/storageKeys.enum';
import { ToastTypesEnum } from '@/enum/toastTypes.enum';

import { useJWT } from '@/hooks/useJWT';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useToast } from '@/hooks/useToast';

import { removeStorageItem } from '@/utils/localStorage';
import { takeRedirectionLink } from '@/utils/ssoRedirect';

const AUTH_PAGES: Array<string> = [RoutesEnum.PROFILE];

export const useAuth = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { jwt, setJWT } = useJWT();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoginLoading, setIsLoginLoading } = useUserContext();

  const { setItem: setUserIDValue } = useLocalStorage<number>(
    StorageKeysEnum.user_id,
  );
  const { setItem: setUsername } = useLocalStorage<string>(
    StorageKeysEnum.username,
  );
  const { value: redirectURL, removeItem: removeRedirectURL } =
    useLocalStorage<string>(StorageKeysEnum.redirectURL);
  const { value: lang } = useLocalStorage<string>(StorageKeysEnum.i18nextLng);
  const { setItem: setToken } = useLocalStorage<string>(StorageKeysEnum.token);

  const token = searchParams.get(SearchParamsEnum.token);

  const isNeedAuth = AUTH_PAGES.includes(pathname);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsernameState] = useState('');

  // API
  const [triggerLogin] = useLoginWithTokenMutation();

  const handleLogin = useCallback(async () => {
    if (isLoginLoading) return;

    if (!token) {
      removeAuthData();
      return;
    }

    // Update the token in the local storage
    setToken(token);

    setIsLoginLoading(true);

    try {
      const data = await triggerLogin({ token }).unwrap();

      setJWT(data?.token);
      setUserIDValue(data?.user_id);

      toast(t(tKeys.successfullyLoggedIn), ToastTypesEnum.success);

      const link = takeRedirectionLink(data);
      if (link) {
        return navigate(link);
      }

      if (redirectURL) {
        const rUrl = redirectURL;
        // NOTE: commented to resolve redirection on GZ after refreshing page
        removeRedirectURL();
        return window.location.assign(
          buildRedirectLink(rUrl, data?.tmp_token, lang, token),
        );
      }
      navigate(RoutesEnum.HOME);
    } catch {
      toast(t(tKeys.errorDuringLogin), ToastTypesEnum.error);
      logout();
    } finally {
      setIsLoginLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, redirectURL, lang]);

  // Set username from user context when available
  useEffect(() => {
    if (user?.username) {
      setUsernameState(user.username);
      setUsername(user.username);
    }
  }, [user, setUsername]);

  useEffect(() => {
    if (isNeedAuth && !jwt) navigate(RoutesEnum.HOME);
  });

  useEffect(() => {
    const token = localStorage.getItem(StorageKeysEnum.jwt);
    const userId = localStorage.getItem(StorageKeysEnum.user_id);
    const storedUsername = localStorage.getItem(StorageKeysEnum.username);

    if (token && userId) {
      setIsAuthenticated(true);
      if (storedUsername) {
        setUsernameState(storedUsername);
      }
    }
  }, [jwt]);

  const removeAuthData = () => {
    console.log('debug: remove auth data');
    removeStorageItem(StorageKeysEnum.jwt);
    removeStorageItem(StorageKeysEnum.token);
    removeStorageItem(StorageKeysEnum.user_id);
    removeStorageItem(StorageKeysEnum.username);
    setIsAuthenticated(false);
    setUsernameState('');
  };

  const logout = () => {
    removeAuthData();
    navigate(RoutesEnum.HOME);
  };

  return { isAuthenticated, username, logout, handleLogin };
};
